import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQuery } from 'shared/api/baseQuery';
import { EApiTags, EPopups, IAppConfig, IBanners, IMeta, TOffersMeta } from 'shared/typings';
import { ELanguages } from 'shared/typings';
import { getCookie } from 'shared/utils/getCookie';

export const commonApi = createApi({
	reducerPath: 'commonApi',
	baseQuery: baseQuery,
	tagTypes: [EApiTags.CONFIG, EApiTags.BANNERS],
	endpoints: (builder) => ({
		getAppConfig: builder.query<IAppConfig, void>({
			query: () => ({
				url: 'app_config/',
			}),
			providesTags: [EApiTags.CONFIG],
		}),
		getMeta: builder.query<IMeta, void>({
			query: () => ({
				url: 'meta/',
			}),
		}),
		getOffersMeta: builder.query<TOffersMeta, string[]>({
			query: (offers) => {
				const offersString = `uuid=${offers.join('&uuid=')}`;
				return {
					url: `meta/offers/?${offersString}`,
				};
			},
		}),
		updatePopups: builder.mutation<void, EPopups>({
			query(body) {
				return {
					url: `account/update_popup_status/${body}/`,
					headers: { 'X-Csrftoken': getCookie('csrftoken') },
					method: 'POST',
				};
			},
			invalidatesTags: (_, error) => (error ? [] : [EApiTags.CONFIG]),
		}),
		getBanners: builder.query<IBanners, void>({
			query: () => {
				return {
					url: `banners/`,
				};
			},
			providesTags: [EApiTags.BANNERS],
			keepUnusedDataFor: 0,
		}),
		closeBanner: builder.mutation<void, string>({
			query(body) {
				return {
					url: `banners/${body}/close/`,
					headers: { 'X-Csrftoken': getCookie('csrftoken') },
					method: 'POST',
				};
			},
			invalidatesTags: (_, error) => (error ? [] : [EApiTags.BANNERS]),
		}),
		updateLanguage: builder.mutation<{ language: ELanguages }, { language: ELanguages }>({
			query(body) {
				return {
					url: `account/language/`,
					headers: { 'X-Csrftoken': getCookie('csrftoken') },
					method: 'POST',
					body,
				};
			},
			invalidatesTags: (_, error) => (error ? [] : [EApiTags.CONFIG]),
		}),
	}),
});

export const {
	useGetAppConfigQuery,
	useGetMetaQuery,
	useGetOffersMetaQuery,
	useUpdateLanguageMutation,
	useUpdatePopupsMutation,
	useGetBannersQuery,
	useCloseBannerMutation,
} = commonApi;
